import { useState, useEffect, useRef } from "react";

const Reading = () => {
  // State variables
  const [authorizationToken, setAuthorizationToken] = useState("");
  const [start, setStart] = useState(false);
  const [stopFlag, setStopFlag] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [points, setPoints] = useState([]);
  const [storyIndex, setStoryIndex] = useState(0);
  const [story, setStory] = useState([]);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");
  const [spokenWords, setSpokenWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  // Refs
  const recognizerRef = useRef(null);
  const SpeechSDKRef = useRef(null);
  const expectedWordsRef = useRef([]);
  const lastProcessedIndexRef = useRef(-1);
  
  // Constants
  const region = "southeastasia";
  const languageRef = useRef("en-US");
  const idRef = useRef("0");

  // Stories data (same as before)
  const stories = [
    [
      "Read aloud the sentences on the screen.",
      "We will follow along your speech and help you learn speak English.",
      "Good luck for your reading lesson!"
    ],
    [
      "The Hare and the Tortoise",
      "Once upon a time, a Hare was making fun of the Tortoise for being so slow.",
      "\"Do you ever get anywhere?\" he asked with a mocking laugh.",
      "\"Yes,\" replied the Tortoise, \"and I get there sooner than you think. Let us run a race.\"",
      "The Hare was amused at the idea of running a race with the Tortoise, but agreed anyway.",
      "So the Fox, who had consented to act as judge, marked the distance and started the runners off.",
      "The Hare was soon far out of sight, and in his overconfidence,",
      "he lay down beside the course to take a nap until the Tortoise should catch up.",
      "Meanwhile, the Tortoise kept going slowly but steadily, and, after some time, passed the place where the Hare was sleeping.",
      "The Hare slept on peacefully; and when at last he did wake up, the Tortoise was near the goal.",
      "The Hare now ran his swiftest, but he could not overtake the Tortoise in time.",
      "Slow and Steady wins the race."
    ],
    [
      "The Ant and The Dove",
      "A Dove saw an Ant fall into a brook.",
      "The Ant struggled in vain to reach the bank,",
      "and in pity, the Dove dropped a blade of straw close beside it.",
      "Clinging to the straw like a shipwrecked sailor, the Ant floated safely to shore.",
      "Soon after, the Ant saw a man getting ready to kill the Dove with a stone.",
      "Just as he cast the stone, the Ant stung the man in the heel, and he missed his aim,",
      "The startled Dove flew to safety in a distant wood and lived to see another day.",
      "A kindness is never wasted."
    ],
    [
      "Es war einmal ein kleines Mädchen namens Anna.",
      "Sie hatte eine Katze namens Luna, die sie über alles liebte.",
      "Eines Tages beschloss Luna, durch das offene Fenster zu entkommen und Anna war sehr traurig.",
      "Sie suchte die ganze Stadt nach Luna ab, aber sie konnte sie nicht finden.", 
      "Schließlich kehrte Luna nach Hause zurück und Anna war so glücklich, sie wiederzusehen."
    ]
  ];

  // Styles (same as before)
  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
    //   maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      background: 'linear-gradient(to bottom, #e6f7ff, #ffffff)',
      borderRadius: '10px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    //   padding:10,
    //   height:'100%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    logo: {
      height: '50px'
    },
    scoreContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
      position: 'relative'
    },
    score: {
      fontSize: '32px',
      fontWeight: 'bold',
      margin: '0 10px 0 0'
    },
    star: {
      color: 'gold',
      fontSize: '32px',
      margin: '0'
    },
    pointsContainer: {
      position: 'absolute',
      right: '30px',
      top: '0'
    },
    point: {
      color: '#4CAF50',
      fontWeight: 'bold',
      animation: 'float 1.5s ease-out forwards',
      position: 'absolute',
      right: '0'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '20px'
    },
    button: {
      padding: '12px 24px',
      fontSize: '18px',
      borderRadius: '25px',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      fontWeight: 'bold',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
    },
    greenButton: {
      backgroundColor: '#4CAF50',
      color: 'white'
    },
    redButton: {
      backgroundColor: '#f44336',
      color: 'white'
    },
    blueButton: {
      backgroundColor: '#2196F3',
      color: 'white'
    },
    buttonIcon: {
      marginRight: '8px'
    },
    readAlongContainer: {
      padding: '20px',
      background: 'white',
      borderRadius: '10px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontSize: '24px',
      lineHeight: '1.5',
      textAlign: 'center'
    },
    spokenWord: {
      color: '#4CAF50',
      fontWeight: 'bold'
    },
    currentWord: {
      color: '#000',
      backgroundColor: '#FFEB3B',
      padding: '0 2px',
      borderRadius: '3px',
      fontWeight: 'bold'
    },
    unspokenWord: {
      color: '#757575'
    },
    warningContainer: {
      textAlign: 'center',
      padding: '20px',
      marginBottom: '20px',
      backgroundColor: '#FFECB3',
      borderRadius: '10px'
    }
  };

  // Debug log function
  const debugLog = (message) => {
    setDebugInfo(prev => `${message}\n${prev}`.slice(0, 500));
    console.log(message);
  };

  // Initialize Speech SDK and load script (same as before)
  useEffect(() => {
    const loadSpeechSDK = () => {
      return new Promise((resolve, reject) => {
        if (window.SpeechSDK) {
          SpeechSDKRef.current = window.SpeechSDK;
          setSdkLoaded(true);
          resolve(window.SpeechSDK);
          return;
        }
        
        const script = document.createElement('script');
        script.src = "https://cdn.jsdelivr.net/npm/microsoft-cognitiveservices-speech-sdk@latest/distrib/browser/microsoft.cognitiveservices.speech.sdk.bundle.min.js";
        script.async = true;
        script.crossOrigin = "anonymous";
        
        script.onload = () => {
          if (window.SpeechSDK) {
            SpeechSDKRef.current = window.SpeechSDK;
            setSdkLoaded(true);
            resolve(window.SpeechSDK);
          } else {
            reject(new Error("Failed to load Speech SDK"));
          }
        };
        
        script.onerror = () => {
          reject(new Error("Failed to load Speech SDK script"));
        };
        
        document.body.appendChild(script);
      });
    };

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext || false;
      if (AudioContext) {
        new AudioContext();
      }
    } catch (e) {
      debugLog("No sound context found: " + e);
    }
    
    const getUrlParam = (name) => {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get(name);
    };
    
    const id = getUrlParam('id') || '0';
    idRef.current = id;
    
    if (id === '3') {
      languageRef.current = 'de-DE';
    }
    
    loadSpeechSDK()
      .then(() => {
        debugLog("Speech SDK loaded successfully");
        loadStory(id);
        getToken();
      })
      .catch(error => {
        debugLog("Error loading Speech SDK: " + error.message);
      });

    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.close();
      }
    };
  }, []);

  // Function to get authorization token (same as before)
  const getToken = () => {
    debugLog("Getting authorization token...");
    const request = new XMLHttpRequest();
    request.open('POST', 'https://southeastasia.api.cognitive.microsoft.com/sts/v1.0/issuetoken', true);
    request.setRequestHeader("Ocp-Apim-Subscription-Key", "97b34a5ebba044cba3b23b297085c0b8");

    request.onload = () => {
      debugLog("Token received successfully");
      setAuthorizationToken(request.responseText);
    };
    
    request.onerror = () => {
      debugLog("Error getting authorization token");
    };
    
    request.send();
  };

  // Function to load story based on ID (modified)
  const loadStory = (id) => {
    debugLog('Loading story with ID: ' + id);
    const storyId = parseInt(id, 10);
    if (storyId < stories.length) {
      debugLog('Story found with ' + stories[storyId].length + ' sentences');
      setStory(stories[storyId]);
      setStoryIndex(0);
      setSpokenWords([]);
      setCurrentWordIndex(0);
      lastProcessedIndexRef.current = -1;
    } else {
      debugLog('No more stories available');
      setSpokenWords([{ word: "--- THE END ---", spoken: true }]);
      setStory([]);
    }
  };

  // Function to add points to score (same as before)
  const addScore = (points) => {
    setTotalScore(prevScore => prevScore + points);
    
    setPoints(prevPoints => {
      const newPoints = [...prevPoints];
      if (newPoints.length >= 5) newPoints.shift();
      newPoints.push((points >= 0 ? "+" : "") + points.toString());
      return newPoints;
    });
    
    debugLog("Score updated: " + points + " points");
  };

  // Function to get next sentence from story (modified)
 const getNextSentence = () => {
  debugLog('Getting next sentence. Current storyIndex: ' + storyIndex + ', Story length: ' + story.length);
  
  if (storyIndex < story.length) {
    const currentSentence = story[storyIndex];
    debugLog('New sentence: ' + currentSentence);
    
    // Create word objects with spoken status
    const words = currentSentence.split(/\s+/).map(word => ({
      word,
      spoken: false
    }));
    
    setSpokenWords(words);
    setCurrentWordIndex(0);
    expectedWordsRef.current = words.map(w => w.word.toLowerCase().replace(/[^\w\s]/g, ''));
    lastProcessedIndexRef.current = -1; // Reset to -1 for new sentence
    setStoryIndex(storyIndex + 1);
    
    return true;
  } else {
    debugLog('No more sentences left in the story');
    return false;
  }
};
  // Function to match spoken word with expected word (completely rewritten)
const matchWord = (cleanSpokenWord) => {
  // Get the current expected word index
  const currentExpectedIndex = lastProcessedIndexRef.current + 1;
  
  // Check if we're at the start of a new sentence
  if (currentExpectedIndex >= expectedWordsRef.current.length) {
    debugLog('No more words expected in this sentence');
    return;
  }

  const expectedWord = expectedWordsRef.current[currentExpectedIndex];
  debugLog(`Comparing "${cleanSpokenWord}" with expected word: "${expectedWord}" (index ${currentExpectedIndex})`);

  // Check if the spoken word matches the expected word
  // Also check if it's a partial match (start of the word)
  if (cleanSpokenWord === expectedWord || 
      expectedWord.startsWith(cleanSpokenWord)) {
    debugLog('MATCH FOUND!');
    
    // Only mark as complete match if the full word is recognized
    const isCompleteMatch = cleanSpokenWord === expectedWord;
    
    if (isCompleteMatch) {
      // Update the spoken words state
      setSpokenWords(prevWords => {
        const newWords = [...prevWords];
        if (currentExpectedIndex < newWords.length) {
          newWords[currentExpectedIndex].spoken = true;
        }
        return newWords;
      });

      // Update the current word index
      lastProcessedIndexRef.current = currentExpectedIndex;
      setCurrentWordIndex(currentExpectedIndex + 1);
      
      addScore(2);

      // Check if we've completed the sentence
      if (currentExpectedIndex + 1 >= expectedWordsRef.current.length) {
        debugLog('Sentence completed!');
        setTimeout(() => {
          if (storyIndex < story.length) {
            getNextSentence();
          } else {
            debugLog("Story finished!");
            stopRecognition();
          }
        }, 500);
      }
    } else {
      // Partial match - just update the current word highlight
      setCurrentWordIndex(currentExpectedIndex);
    }
  } else {
    debugLog(`NO MATCH: "${cleanSpokenWord}" != "${expectedWord}"`);
    addScore(-1);
  }
};
  // Function to stop speech recognition (same as before)
  const stopRecognition = () => {
    debugLog("Stopping recognition");
    setStart(false);
    setStopFlag(true);
    
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          recognizerRef.current.close();
          recognizerRef.current = null;
          debugLog("Recognition stopped successfully");
        },
        (err) => {
          debugLog("Error stopping recognition: " + err);
          recognizerRef.current.close();
          recognizerRef.current = null;
        }
      );
    }
  };

  // Reset everything for a new story (same as before)
  const resetForNewStory = () => {
    debugLog("Resetting for new story");
    setStart(false);
    setStopFlag(false);
    setStoryIndex(0);
    setSpokenWords([]);
    setCurrentWordIndex(0);
    lastProcessedIndexRef.current = -1;
    
    getToken();
    const newId = parseInt(idRef.current) + 1;
    idRef.current = newId.toString();
    loadStory(newId);
  };

  // Handle button click (same as before)
  const handleButtonClick = () => {
    debugLog('Button clicked. Current state - SDK loaded: ' + sdkLoaded + ', Start: ' + start + ', Stop flag: ' + stopFlag);
    
    if (!sdkLoaded) return;
    
    if (stopFlag) {
      resetForNewStory();
    } else if (start) {
      stopRecognition();
    } else {
      setStart(true);
      startRecognition();
    }
  };

  // Start speech recognition (modified)
  const startRecognition = () => {
    debugLog('Preparing for speech recognition');
    if (!SpeechSDKRef.current) {
      debugLog("Speech SDK not found");
      return;
    }

    // Load first sentence if needed
    if (spokenWords.length === 0 || spokenWords.every(w => w.spoken)) {
      debugLog("Getting first sentence before starting recognition");
      getNextSentence();
    }

    const audioConfig = SpeechSDKRef.current.AudioConfig.fromDefaultMicrophoneInput();
    let speechConfig;
    
    if (authorizationToken) {
      speechConfig = SpeechSDKRef.current.SpeechConfig.fromAuthorizationToken(authorizationToken, region);
      debugLog('Created speech config with token');
    } else {
      debugLog("Authorization token not available");
      return;
    }

    speechConfig.speechRecognitionLanguage = languageRef.current;
    debugLog('Using language: ' + languageRef.current);
    
    const recognizer = new SpeechSDKRef.current.SpeechRecognizer(speechConfig, audioConfig);
    debugLog('Created recognizer');
    
    try {
      const phraseListGrammar = SpeechSDKRef.current.PhraseListGrammar.fromRecognizer(recognizer);
      debugLog('Created phrase list grammar');
      
      story.forEach(sentence => {
        phraseListGrammar.addPhrase(sentence);
      });
      debugLog('Added phrases to grammar');
    } catch (error) {
      debugLog('Error setting up phrase list grammar: ' + error.message);
    }

    recognizerRef.current = recognizer;

recognizer.recognizing = (s, e) => {
  if (!e.result.text) return;
  
  debugLog('Recognizing: ' + e.result.text);
  const words = e.result.text.split(' ').filter(w => w.trim() !== '');
  
  // If we only have one word, process it immediately
  if (words.length === 1) {
    const cleanWord = words[0].toLowerCase().replace(/[^\w\s]/g, '');
    if (cleanWord) {
      matchWord(cleanWord);
    }
  } 
  // For multiple words, process each one
  else {
    words.forEach(spokenWord => {
      if (lastProcessedIndexRef.current < expectedWordsRef.current.length - 1) {
        const cleanWord = spokenWord.toLowerCase().replace(/[^\w\s]/g, '');
        if (cleanWord) {
          matchWord(cleanWord);
        }
      }
    });
  }
};

    recognizer.recognized = (s, e) => {
      debugLog('Recognized: ' + e.result.text);
    };

    recognizer.canceled = (s, e) => {
      debugLog(`CANCELED: Reason=${e.reason}`);
      if (e.reason === SpeechSDKRef.current.CancellationReason.Error) {
        debugLog(`ERROR: ${e.errorDetails}`);
      }
      recognizerRef.current.close();
    };

    recognizer.startContinuousRecognitionAsync(
      () => debugLog("Recognition started successfully"),
      (err) => {
        debugLog("Error starting recognition: " + err);
        recognizerRef.current = null;
      }
    );
  };

  // Render highlighted text (modified)
  const renderHighlightText = () => {
  return spokenWords.map((wordObj, index) => {
    let style;
    if (wordObj.spoken) {
      style = styles.spokenWord; // Green for spoken words
    } else if (index === currentWordIndex) {
      style = styles.currentWord; // Yellow background for current word
    } else {
      style = styles.unspokenWord; // Gray for unspoken words
    }
    
    return (
      <span key={index} style={style}>
        {wordObj.word}{' '}
      </span>
    );
  });
};
  // Animation keyframes for points (same as before)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0% {
          transform: translateY(0);
          opacity: 1;
        }
        100% {
          transform: translateY(-50px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Render points animation (same as before)
  const renderPoints = () => {
    return points.map((point, index) => (
      <div key={index} style={{
        ...styles.point,
        animation: 'float 1.5s ease-out forwards',
        top: `${index * 25}px`
      }}>
        {point}
      </div>
    ));
  };

  // Button text and class based on state (same as before)
  const getButtonText = () => {
    if (stopFlag) return (
      <>
        <span style={styles.buttonIcon}>➡️</span>
        Next
      </>
    );
    if (start) return (
      <>
        <span style={styles.buttonIcon}>⏹️</span>
        Stop
      </>
    );
    return (
      <>
        <span style={styles.buttonIcon}>🎤</span>
        Start
      </>
    );
  };

  const getButtonStyle = () => {
    if (stopFlag) return { ...styles.button, ...styles.blueButton };
    if (start) return { ...styles.button, ...styles.redButton };
    return { ...styles.button, ...styles.greenButton };
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <img src="/api/placeholder/150/50" alt="Young Minds Logo" style={styles.logo} />
        </div>
        <div style={styles.scoreContainer}>
          <p style={styles.score}>{totalScore}</p>
          <p style={styles.star}>⭐</p>
          <div style={styles.pointsContainer}>{renderPoints()}</div>
        </div>
      </header>
      
      {!sdkLoaded && (
        <div style={styles.warningContainer}>
          <h1 style={{ fontWeight: 500 }}>
            Loading Speech Recognition SDK... Please wait.
          </h1>
        </div>
      )}
      
      <div style={styles.buttonContainer}>
        <button 
          style={getButtonStyle()}
          onClick={handleButtonClick}
          disabled={!sdkLoaded}
        >
          {getButtonText()}
        </button>
      </div>
      
      <div style={styles.readAlongContainer}>
        {renderHighlightText()}
      </div>
    </div>
  );
};

export default Reading;